/**
 * Shopify Cart Integration Module
 * Handles Shopify Ajax Cart API interactions
 */

(function(window) {
    'use strict';

    // Product ID Mapping (Internal ID → Shopify Product ID)
    const PRODUCT_MAP = {
        '2914': '8151292772409', // ARTHUS
        '2913': '8151292313657', // BOND
        '2915': '8151293001785'  // ARTHUS BOY
    };

    // Shopify domain (will be set from meta tag or config)
    let shopifyDomain = window.Shopify ? window.Shopify.shop : null;

    /**
     * Get Shopify product ID from internal variant ID or from DOM
     * @param {string} internalId - Internal variant ID (now actually Shopify product ID)
     * @returns {string} - Shopify product ID
     */
    function getShopifyProductId(internalId) {
        // Artık internalId zaten Shopify product ID olabilir (Liquid'den geliyor)
        // Eğer PRODUCT_MAP'te yoksa, direkt kullan
        const productId = PRODUCT_MAP[internalId] || internalId;
        
        // DOM'dan da kontrol et
        const addToCartButton = document.querySelector(`[data-productid="${internalId}"]`);
        if (addToCartButton) {
            const dataProductId = addToCartButton.closest('.product-variant-line')?.getAttribute('data-productid');
            if (dataProductId) {
                console.log(`✅ Product ID DOM'dan alındı: ${dataProductId}`);
                return dataProductId;
            }
        }
        
        if (!productId) {
            throw new Error(`Ürün ID'si bulunamadı: ${internalId}`);
        }
        return productId;
    }

    /**
     * Fetch product variants from Shopify using HANDLE (not ID)
     * @param {string} productHandle - Shopify product handle
     * @returns {Promise<Array>} - Array of variants
     */
    async function fetchProductVariants(productHandle) {
        try {
            console.log(`📦 Fetching product variants for handle: ${productHandle}`);
            const response = await fetch(`/products/${productHandle}.js`);
            if (!response.ok) {
                throw new Error('Ürün bilgileri alınamadı');
            }
            const product = await response.json();
            console.log(`✅ Product fetched:`, product);
            return product.variants || [];
        } catch (error) {
            console.error('Fetch variants error:', error);
            throw new Error('Ürün varyantları yüklenemedi');
        }
    }

    /**
     * Get variant ID by size using product HANDLE
     * @param {string} productHandle - Shopify product handle
     * @param {string} size - Selected size (S, M, L, XL, etc.)
     * @returns {Promise<string>} - Shopify variant ID
     */
    async function getVariantIdBySize(productHandle, size) {
        try {
            console.log(`🔍 Getting variant for handle: ${productHandle}, size: ${size}`);
            
            // Fetch variants using handle
            const variants = await fetchProductVariants(productHandle);
            
            console.log(`   Found ${variants.length} variants`);
            
            // Find variant with matching size
            const variant = variants.find(v => {
                // Check if variant has option matching the size
                return v.options && v.options.some(option => 
                    option.toString().toUpperCase() === size.toUpperCase()
                );
            });

            if (!variant) {
                // If no variant found, try matching by title
                const variantByTitle = variants.find(v => 
                    v.title && v.title.toUpperCase().includes(size.toUpperCase())
                );
                
                if (variantByTitle) {
                    console.log(`✅ Variant found by title: ${variantByTitle.id}`);
                    return variantByTitle.id.toString();
                }
                
                console.error(`❌ Variant not found for size: ${size}`);
                console.log('Available variants:', variants.map(v => ({ id: v.id, title: v.title, options: v.options })));
                throw new Error(`Seçilen beden için ürün bulunamadı: ${size}`);
            }

            console.log(`✅ Variant found: ${variant.id}`);
            return variant.id.toString();
        } catch (error) {
            console.error('Get variant ID error:', error);
            throw error;
        }
    }

    /**
     * Add product to Shopify cart
     * @param {Object} cartData - Cart data object
     * @returns {Promise<Object>} - Cart response
     */
    async function addToCart(cartData) {
        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(cartData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.description || 'Sepete eklenemedi');
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Add to cart error:', error);
            throw error;
        }
    }

    /**
     * Add to cart with retry logic
     * @param {Object} cartData - Cart data
     * @param {number} maxRetries - Maximum retry attempts
     * @returns {Promise<Object>}
     */
    async function addToCartWithRetry(cartData, maxRetries = 3) {
        let lastError;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const result = await addToCart(cartData);
                return result;
            } catch (error) {
                lastError = error;
                console.warn(`Add to cart attempt ${attempt} failed:`, error.message);
                
                if (attempt < maxRetries) {
                    // Wait before retry (exponential backoff)
                    const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
                    await sleep(delay);
                }
            }
        }
        
        throw lastError;
    }

    /**
     * Get current cart state
     * @returns {Promise<Object>}
     */
    async function getCart() {
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) {
                throw new Error('Sepet bilgileri alınamadı');
            }
            return await response.json();
        } catch (error) {
            console.error('Get cart error:', error);
            throw error;
        }
    }

    /**
     * Format line item properties
     * @param {Object} customData - Custom product data
     * @returns {Object} - Formatted properties
     */
    function formatLineItemProperties(customData) {
        const properties = {};

        // Add original image URL (hidden property with underscore)
        if (customData.originalImageUrl) {
            properties['_Original Image'] = customData.originalImageUrl;
        }

        // Add rendered image URL
        if (customData.renderedImageUrl) {
            properties['_Rendered Image'] = customData.renderedImageUrl;
        }

        // Add custom text
        if (customData.customText) {
            properties['_Custom Text'] = customData.customText;
        }

        // Add text position
        if (customData.textPosition) {
            properties['_Text Position'] = customData.textPosition;
        }

        // Add text color
        if (customData.textColor) {
            properties['_Text Color'] = customData.textColor;
        }

        // Add upload timestamp
        properties['_Upload Date'] = new Date().toISOString();

        // Add variant name
        if (customData.variantName) {
            properties['_Product Variant'] = customData.variantName;
        }

        return properties;
    }

    /**
     * Prepare cart data for Shopify
     * @param {string} variantId - Shopify variant ID
     * @param {number} quantity - Quantity
     * @param {Object} customData - Custom product data
     * @returns {Object} - Shopify cart data
     */
    function prepareCartData(variantId, quantity = 1, customData = {}) {
        return {
            id: variantId,
            quantity: quantity,
            properties: formatLineItemProperties(customData)
        };
    }

    /**
     * Main function: Add custom product to cart
     * @param {string} productHandle - Product handle (e.g., 'bond-custom-custom-deniz-sortu')
     * @param {string} size - Selected size
     * @param {Object} customData - Custom product data
     * @returns {Promise<Object>} - Cart response
     */
    async function addCustomProductToCart(productHandle, size, customData) {
        try {
            console.log(`🛒 Adding to cart - Handle: ${productHandle}, Size: ${size}`);

            // 1. Get Shopify variant ID by size using handle
            const variantId = await getVariantIdBySize(productHandle, size);

            // 2. Prepare cart data
            const cartData = prepareCartData(variantId, 1, customData);

            // 3. Add to cart with retry
            const result = await addToCartWithRetry(cartData);

            console.log(`✅ Added to cart successfully:`, result);
            return result;
        } catch (error) {
            console.error('Add custom product to cart error:', error);
            throw error;
        }
    }

    /**
     * Sleep utility
     * @param {number} ms
     * @returns {Promise}
     */
    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Redirect to cart page
     */
    function redirectToCart() {
        window.location.href = '/cart';
    }

    /**
     * Redirect to checkout
     */
    function redirectToCheckout() {
        window.location.href = '/checkout';
    }

    /**
     * Show cart drawer (if theme supports it)
     */
    function showCartDrawer() {
        // Try to trigger cart drawer if available
        if (window.theme && typeof window.theme.openCart === 'function') {
            window.theme.openCart();
        } else if (document.querySelector('.cart-drawer')) {
            const cartDrawer = document.querySelector('.cart-drawer');
            cartDrawer.classList.add('active');
        } else {
            // Fallback to redirect
            redirectToCart();
        }
    }

    /**
     * Get cart count
     * @returns {Promise<number>}
     */
    async function getCartCount() {
        try {
            const cart = await getCart();
            return cart.item_count || 0;
        } catch (error) {
            console.error('Get cart count error:', error);
            return 0;
        }
    }

    /**
     * Update cart count display
     */
    async function updateCartCountDisplay() {
        try {
            const count = await getCartCount();
            const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
            
            cartCountElements.forEach(element => {
                element.textContent = count;
                if (count > 0) {
                    element.classList.add('has-items');
                } else {
                    element.classList.remove('has-items');
                }
            });
        } catch (error) {
            console.error('Update cart count error:', error);
        }
    }

    // Export to global scope
    window.ShopifyCart = {
        // Core functions
        addCustomProductToCart: addCustomProductToCart,
        addToCart: addToCart,
        getCart: getCart,
        getCartCount: getCartCount,
        
        // Utility functions
        getShopifyProductId: getShopifyProductId,
        getVariantIdBySize: getVariantIdBySize,
        fetchProductVariants: fetchProductVariants,
        prepareCartData: prepareCartData,
        formatLineItemProperties: formatLineItemProperties,
        
        // Navigation functions
        redirectToCart: redirectToCart,
        redirectToCheckout: redirectToCheckout,
        showCartDrawer: showCartDrawer,
        updateCartCountDisplay: updateCartCountDisplay,
        
        // Configuration
        PRODUCT_MAP: PRODUCT_MAP
    };

})(window);
